import { isNotDefined } from '@sgme/fp';
import { useEffect, useRef, useState } from 'react';

/**
 * Minimum width reserved for the input field to ensure it remains usable
 */
const MIN_INPUT_WIDTH = 100;

/**
 * Delay in milliseconds to wait for DOM rendering before calculating badge visibility
 */
const DOM_RENDER_DELAY_MS = 50;

/**
 * Minimum number of badges to always display when items are selected
 */
const MIN_VISIBLE_BADGES = 1;

/**
 * Debounce delay for resize calculations (60fps = ~16ms)
 */
const RESIZE_DEBOUNCE_MS = 16;

/**
 * Hook to dynamically calculate the number of visible badges
 * to keep the SgPicker on a single line
 *
 * @param badgesCount - Total number of selected badges
 * @param selectedItemsDisplay - Display mode for selected items
 * @returns Object containing the number of visible badges and the parent container ref
 */
export function useTruncateBadges(
  badgesCount: number,
  selectedItemsDisplay?: 'count' | 'badges' | 'truncate',
  inputHeight?: number,
) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const badgesContainerRef = useRef<HTMLDivElement>(null);
  const [visibleBadgesCount, setVisibleBadgesCount] = useState(badgesCount);

  // Cache for optimization
  const prevWidthRef = useRef(0);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const counterBadgeRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    // Only calculate if truncate mode is enabled
    if (selectedItemsDisplay !== 'truncate' || badgesCount === 0) {
      setVisibleBadgesCount(badgesCount);

      if (typeof inputHeight === 'number' && wrapperRef.current) {
        wrapperRef.current.style.cssText = `max-height: ${inputHeight}px !important;`;
      }

      return undefined;
    }

    const calculateVisibleBadges = () => {
      const wrapper = wrapperRef.current;
      const badgesContainer = badgesContainerRef.current;

      if (!wrapper || !badgesContainer) {
        return;
      }

      // Get all badge elements
      const badges = Array.from(badgesContainer.querySelectorAll('.badge-item')) as HTMLElement[];

      if (badges.length === 0) {
        setVisibleBadgesCount(badgesCount);
        return;
      }

      // Get computed styles for the wrapper to calculate real padding values
      const wrapperStyles = getComputedStyle(wrapper);
      const wrapperPaddingLeft = Number.parseFloat(wrapperStyles.paddingLeft) || 0;
      const wrapperPaddingRight = Number.parseFloat(wrapperStyles.paddingRight) || 0;
      const wrapperPadding = wrapperPaddingLeft + wrapperPaddingRight;

      // Available width in the wrapper (excluding padding)
      const wrapperWidth = wrapper.offsetWidth - wrapperPadding;

      // Optimization: Skip calculation if width hasn't changed
      if (wrapperWidth === prevWidthRef.current && visibleBadgesCount > 0) {
        return;
      }
      prevWidthRef.current = wrapperWidth;

      // Calculate space occupied by other elements (icon, clear button, dropdown indicator)
      const icon = wrapper.querySelector('.icon');
      const clearButton = wrapper.querySelector('.clear-button');
      const dropdownIndicator = wrapper.querySelector('.dropdown-indicator');

      let reservedSpace = 0;

      if (icon) {
        const iconElement = icon as HTMLElement;
        const iconStyles = getComputedStyle(iconElement);
        const iconMarginLeft = Number.parseFloat(iconStyles.marginLeft) || 0;
        const iconMarginRight = Number.parseFloat(iconStyles.marginRight) || 0;
        reservedSpace += iconElement.offsetWidth + iconMarginLeft + iconMarginRight;
      }

      if (clearButton) {
        const clearButtonElement = clearButton as HTMLElement;
        const clearButtonStyles = getComputedStyle(clearButtonElement);
        const clearButtonMarginLeft = Number.parseFloat(clearButtonStyles.marginLeft) || 0;
        const clearButtonMarginRight = Number.parseFloat(clearButtonStyles.marginRight) || 0;
        reservedSpace += clearButtonElement.offsetWidth + clearButtonMarginLeft + clearButtonMarginRight;
      }

      if (dropdownIndicator) {
        const dropdownElement = dropdownIndicator as HTMLElement;
        const dropdownStyles = getComputedStyle(dropdownElement);
        const dropdownMarginLeft = Number.parseFloat(dropdownStyles.marginLeft) || 0;
        const dropdownMarginRight = Number.parseFloat(dropdownStyles.marginRight) || 0;
        reservedSpace += dropdownElement.offsetWidth + dropdownMarginLeft + dropdownMarginRight;
      }

      // Reserve minimum space for the input field
      reservedSpace += MIN_INPUT_WIDTH;

      // Available width for badges
      const availableWidth = wrapperWidth - reservedSpace;

      // Dynamically measure the counter badge width
      // Reuse or create the temporary counter badge
      if (!counterBadgeRef.current) {
        counterBadgeRef.current = document.createElement('span');
        counterBadgeRef.current.className = 'badge bg-secondary';
        counterBadgeRef.current.style.visibility = 'hidden';
        counterBadgeRef.current.style.position = 'absolute';
      }

      const tempCounterBadge = counterBadgeRef.current;
      tempCounterBadge.textContent = `+${badgesCount}`;
      badgesContainer.appendChild(tempCounterBadge);

      const counterBadgeWidth = tempCounterBadge.offsetWidth;
      const counterBadgeStyles = getComputedStyle(tempCounterBadge);
      const counterBadgeMarginLeft = Number.parseFloat(counterBadgeStyles.marginLeft) || 0;
      const counterBadgeMarginRight = Number.parseFloat(counterBadgeStyles.marginRight) || 0;
      const counterBadgeTotalWidth = counterBadgeWidth + counterBadgeMarginLeft + counterBadgeMarginRight;

      badgesContainer.removeChild(tempCounterBadge);

      // Calculate how many badges can fit
      let totalWidth = 0;
      let count = 0;

      for (const badge of badges) {
        if (isNotDefined(badge)) {
          continue;
        }

        const badgeStyles = getComputedStyle(badge);
        const badgeMarginLeft = Number.parseFloat(badgeStyles.marginLeft) || 0;
        const badgeMarginRight = Number.parseFloat(badgeStyles.marginRight) || 0;
        const badgeTotalWidth = badge.offsetWidth + badgeMarginLeft + badgeMarginRight;

        // Check if we need the counter badge
        const needsCounterBadge = count + 1 < badgesCount;
        const widthWithCounter = totalWidth + badgeTotalWidth + (needsCounterBadge ? counterBadgeTotalWidth : 0);

        // If it overflows, stop
        if (widthWithCounter > availableWidth && count > 0) {
          break;
        }

        totalWidth += badgeTotalWidth;
        count++;
      }

      // Ensure at least MIN_VISIBLE_BADGES badge is displayed if items are selected
      const finalCount = Math.max(MIN_VISIBLE_BADGES, Math.min(count, badgesCount));
      setVisibleBadgesCount(finalCount);
    };

    // Calculate after a short delay to ensure the DOM is rendered
    const timeoutId = setTimeout(calculateVisibleBadges, DOM_RENDER_DELAY_MS);

    // Debounced calculation for resize events
    const debouncedCalculate = () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        requestAnimationFrame(calculateVisibleBadges);
      }, RESIZE_DEBOUNCE_MS);
    };

    // Observe size changes of the wrapper (form-control)
    const resizeObserver = new ResizeObserver(debouncedCalculate);

    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      resizeObserver.disconnect();
      // Cleanup counter badge reference
      counterBadgeRef.current = null;
    };
  }, [badgesCount, selectedItemsDisplay, visibleBadgesCount, inputHeight]);

  return {
    wrapperRef,
    badgesContainerRef,
    visibleBadgesCount: selectedItemsDisplay === 'truncate' ? visibleBadgesCount : badgesCount,
  };
}
